/**
 * PaymentSuccess — landing page after a successful Stripe Checkout.
 * Polls /api/payments/status/{session_id} until the server confirms
 * `payment_status: paid` (webhook may lag a few seconds). Shows a
 * confirmation and a button back to the dashboard once activated.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { paymentsAPI } from '../lib/api';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [tier, setTier] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    const maxAttempts = 10;      // ~20 seconds total
    let cancelled = false;

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await paymentsAPI.getStatus(sessionId);
        if (cancelled) return;
        setTier(data.plan_tier);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          // Tell the TrialBanner (and anyone else listening) to refetch
          // their billing state so the trial banner disappears without
          // a page reload.
          window.dispatchEvent(new CustomEvent('elara:billing-updated'));
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed');
          return;
        }
      } catch (e) {
        // Treat as transient and keep polling.
      }
      if (!cancelled && attempts < maxAttempts) {
        setTimeout(tick, 2000);
      } else if (!cancelled) {
        setStatus('timeout');
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-[#1A1A1A] text-[#F2F2F2] flex items-center justify-center p-6"
    >
      <div className="max-w-md w-full bg-[#2B2B2B] rounded-xl p-8 border border-white/10 text-center">
        {status === 'polling' && (
          <>
            <Loader2 className="w-14 h-14 text-[#1ABC9C] mx-auto animate-spin" />
            <h1 className="text-2xl font-semibold mt-5">Confirming your payment…</h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              This usually takes a few seconds. Please don’t close this window.
            </p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#1ABC9C] mx-auto" />
            <h1 className="text-2xl font-semibold mt-5" data-testid="payment-success-title">
              Welcome to Elara{tier ? ` ${tier[0].toUpperCase()}${tier.slice(1)}` : ''}!
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Your subscription is active. You can manage billing any time from Settings → Billing.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="mt-6 bg-[#1ABC9C] text-black hover:bg-[#1ABC9C]/90"
              data-testid="payment-success-continue-btn"
            >
              Continue to dashboard
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <h1 className="text-2xl font-semibold mt-5">Payment did not complete</h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Your card was not charged. You can try again from the upgrade dialog.
            </p>
            <Button onClick={() => navigate('/')} className="mt-6" variant="outline">
              Back to dashboard
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <h1 className="text-2xl font-semibold mt-5">Still finalising…</h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Stripe is taking a little longer than usual. We’ll activate your
              workspace automatically once the payment lands.
            </p>
            <Button onClick={() => navigate('/')} className="mt-6" variant="outline">
              Back to dashboard
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-2xl font-semibold mt-5">Something went wrong</h1>
            <p className="text-sm text-[#A1A1AA] mt-2">
              We couldn’t find this checkout session. Please try again.
            </p>
            <Button onClick={() => navigate('/')} className="mt-6" variant="outline">
              Back to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
